import type { Unit, UnitItem } from "@sins2-atlas/game-data";
import { exoticsById, itemsById } from "../lib/dataset";
import { exoticComponentNote } from "../lib/disclosures";
import { exactNumber, priceParts } from "../lib/format";
import { researchSubjectName } from "../lib/tech-index";
import { FACTION_LABELS } from "../lib/vocabulary";
import { AbilityEffects } from "./ability-effects";
import { ArtImage } from "./art-image";
import { Card } from "./card";
import { Chip } from "./chip";

// The entity page's item shop listing (Issue 14): every item the unit can buy, grouped
// the way the game's shop groups them, with each item's own facts from the game files.
// Units without a shop render nothing (no empty section).

/** "Requires <research name>" text: the id joins to the research tree's own subject name. */
function researchNames(ids: readonly string[]): string {
  return ids.map((id) => researchSubjectName(id) ?? id).join(", ");
}

function itemNames(ids: readonly string[]): string {
  return ids.map((id) => itemsById.get(id)?.name ?? id).join(", ");
}

/** The game's own sub-headings, from en.localized_text (`tooltip.unit_item_*`), used to group
 * an item's lines the way its in-game tooltip does (ADR 0005). The game's third heading,
 * "On Self:" (`tooltip.unit_item_self_label`), is deliberately absent: it distinguishes
 * self-targeted modifiers from the rest, and the dataset does not carry that distinction, so
 * shipping it would mean guessing which lines belong under it. */
const PASSIVE_HEADING = "Passive Bonus:";
const ACTIVE_HEADING = "Active Action:";

function ItemHeading({ children }: { children: string }) {
  return (
    <p className="pt-0.5 font-mono text-[11.5px] uppercase tracking-[0.1em] text-muted-2">
      {children}
    </p>
  );
}

/** Exotics, then Price, then Max. Count — the game's own order on an item tooltip. Build
 * time has no place in that sequence (the game does not show it), so it trails the price it
 * belongs with rather than being dropped: ADR 0005 reorders, it never removes a value. */
function ItemCosts({ item }: { item: UnitItem }) {
  const parts = priceParts(item.price);
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[12.5px] text-ink-3 [font-variant-numeric:tabular-nums]">
      {item.exoticPrice.map((exotic) => {
        const info = exoticsById.get(exotic.exoticType);
        return (
          <span
            key={exotic.exoticType}
            className="flex items-center gap-1"
            title={exoticComponentNote(exotic)}
          >
            {info && <ArtImage asset={info.smallIcon} alt="" className="h-4 w-4" />}
            {exotic.count}
          </span>
        );
      })}
      {parts.length > 0 && <span>{parts.join(", ")}</span>}
      <span className="text-muted-2">{exactNumber(item.buildTime)}s</span>
      {item.maxCountOnUnit !== undefined && (
        <span className="text-muted-2">max {item.maxCountOnUnit}</span>
      )}
    </span>
  );
}

function ItemRow({ unit, item }: { unit: Unit; item: UnitItem }) {
  // A single-faction item on a both-factions hull gets the faction chip, mirroring the
  // roster Name cell's exclusivity rule.
  const exclusive =
    item.factions.length === 1 && unit.factions.length > 1 ? item.factions[0] : undefined;
  const factionLabel = exclusive ? FACTION_LABELS[unit.race][exclusive] : undefined;
  const unlocked = unit.weapons.filter((w) => w.requiredItemId === item.id);
  const leveled = item.levels.length > 1;
  return (
    <li className="flex gap-3 py-3">
      <ArtImage
        asset={item.hudIcon}
        alt=""
        className="mt-0.5 h-8 w-8 shrink-0 rounded border border-line-faint"
      />
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-[14.5px] font-medium text-ink">{item.name}</span>
          {/* Roomier than the shared box on both axes: this chip sits in a row of 14.5px
              item names, not in a table cell. */}
          {factionLabel && <Chip label={factionLabel.full} className="px-1.5 py-0.5!" />}
          <ItemCosts item={item} />
        </div>
        {/* Game Order (ADR 0005): the gate comes straight after the name, under the game's
            own label, instead of trailing at the bottom as "Requires …". */}
        {item.buildPrerequisites && (
          <p className="font-mono text-[12.5px] text-muted-2">
            Research Required: {researchNames(item.buildPrerequisites)}
          </p>
        )}
        {item.levels.some((level) => level.effects.length > 0) && (
          <ItemHeading>{PASSIVE_HEADING}</ItemHeading>
        )}
        {item.levels.map((level, index) => {
          const label = leveled ? `Level ${index + 1}` : undefined;
          const notes = [
            ...(level.prerequisites === undefined
              ? []
              : [`requires ${researchNames(level.prerequisites)}`]),
            ...(level.consumableStackCount === undefined
              ? []
              : [
                  `${level.consumableStackCount} ${level.consumableStackCount === 1 ? "use" : "uses"}`,
                ]),
          ];
          // A level with nothing to say renders nothing — a bare "Level 1:" label would
          // dangle (the Meteor Storm case).
          if (level.effects.length === 0 && notes.length === 0) return null;
          return (
            <p
              // biome-ignore lint/suspicious/noArrayIndexKey: the level number IS the identity — item levels are positional by definition and never reorder.
              key={`level-${index + 1}`}
              className="font-mono text-[12.5px] text-ink-2 [font-variant-numeric:tabular-nums]"
            >
              {label && <span className="text-muted-2">{label}: </span>}
              {level.effects.join(" · ")}
              {level.effects.length > 0 && notes.length > 0 && " "}
              {notes.length > 0 && <span className="text-muted-2">({notes.join(", ")})</span>}
            </p>
          );
        })}
        {item.ability && (
          // Only the granted ability's NUMBERS appear here. Its name and description are
          // deliberately not repeated: measured 17/17, every item ability that carries them
          // carries exactly the item's own words, so the listing printed each item's name
          // and description twice in a row. The other 45 have none of their own at all.
          <>
            <ItemHeading>{ACTIVE_HEADING}</ItemHeading>
            <div className="max-w-[64ch]">
              <AbilityEffects ability={item.ability} />
            </div>
          </>
        )}
        {unlocked.length > 0 && (
          <p className="text-[13px] text-ink-3">
            Unlocks{" "}
            {unlocked
              .map((w) => `${w.name} (${w.count} ${w.count === 1 ? "mount" : "mounts"})`)
              .join(", ")}
          </p>
        )}
        {item.requiresWeapon && (
          <p className="text-[12.5px] text-muted-2">Requires a {item.requiresWeapon.name} weapon</p>
        )}
        {item.neededItems && (
          <p className="text-[12.5px] text-muted-2">Needs {itemNames(item.neededItems)}</p>
        )}
        {item.mutuallyExclusiveItems && (
          // The game's own phrase (tooltip.item_prerequisites_mutually_exclusive_items_line_begin_text).
          <p className="text-[12.5px] text-muted-2">
            Cannot be combined with: {itemNames(item.mutuallyExclusiveItems)}
          </p>
        )}
        {/* LAST, as on every game tooltip — items, units and research alike (ADR 0005). It
            read 2nd here, which pushed the numbers a reader came for below the prose. */}
        {item.description !== undefined && (
          <p className="max-w-[64ch] whitespace-pre-line text-[13.5px] text-ink-3">
            {item.description}
          </p>
        )}
      </div>
    </li>
  );
}

export function UnitItemsSection({ unit }: { unit: Unit }) {
  if (unit.items.length === 0) return null;
  const items = unit.items
    .map((id) => itemsById.get(id))
    .filter((item): item is UnitItem => item !== undefined);
  const groups: { id: string; name: string; items: UnitItem[] }[] = [];
  for (const item of items) {
    const group = groups.find((g) => g.id === item.buildGroup.id);
    if (group) group.items.push(item);
    else groups.push({ id: item.buildGroup.id, name: item.buildGroup.name, items: [item] });
  }
  return (
    <Card title="Items">
      <div className="space-y-4 px-6 py-4">
        {groups.map((group) => (
          <div key={group.id}>
            <h3 className="font-mono text-[11.5px] uppercase tracking-[0.12em] text-muted-2">
              {group.name}
            </h3>
            <ul className="flex flex-col divide-y divide-line-faint">
              {group.items.map((item) => (
                <ItemRow key={item.id} unit={unit} item={item} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}
